import {
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit,
  ViewEncapsulation,
} from "@angular/core";
import { NgForm } from "@angular/forms";
import { NotificationService } from "@bpm/services/notification.service";
import { QueueService } from "@bpm/services/queue.service";
import { CollectionUser, Menu, StatusChange, User } from "@common/models";
import { Notification } from "@common/models/approval-flow";
import { AppPermissionsService, AuthenticationService, StatusChangeService, UserService } from "@common/services";
import { InsightsService } from "@core/services";
import { environment } from "environments/environment";
import { CollectionUserService } from "@common/services/collection-user";
import { CollectionService } from "@common/services/collection.service";
import { Order } from "@core/models/datables";
import { NgbActiveModal } from "@ng-bootstrap/ng-bootstrap";
import { TranslateService } from "@ngx-translate/core";
import { ProdFormula } from "@ryd/models";
import { ColumnMode } from "@swimlane/ngx-datatable";
import { CustomSwalService } from "app/helpers";
import { BlockUI, NgBlockUI } from "ng-block-ui";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";

@Component({
    selector: "approval-flow",
    templateUrl: "./approval-flow.component.html",
    styleUrls: ["./approval-flow.component.scss"],
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class ApprovalFlowComponent implements OnInit, OnDestroy {
  public statusList: StatusChange[];
  public userLoading: boolean = false;
  public collectionUser: CollectionUser[];
  public langEnglish: boolean = false;
  private _unsubscribeAll: Subject<any>;
  public row: Array<Notification> = [];
  public menu: Menu;

  // Generals
  viewUserAvailableList: any[];
  viewUsersLoading: boolean = false;
  public userSelected: { users: any[] }[] = [];
  public quantityPerPage: number = 5;
  public pageNumber: number = 0;
  public filterText: string = "";
  public ColumnMode = ColumnMode;

  // Escape no cierra el modal: lo resuelve keyboard: false al abrirlo
  @BlockUI("section-block-collection-user")
  sectionBlockUI: NgBlockUI;
  @Input() menuId: number;
  @Input() object: any;
  @Input() canClose: boolean = false;
  //usuarios que se encuentran agregados en la notificaciones de la formula
  @Input() public userSelectedInput: Notification[] = [];

  constructor(
    private _cdr: ChangeDetectorRef,
    private _appPermissionService: AppPermissionsService,
    private _statushChangeServive: StatusChangeService,
    private _swalService: CustomSwalService,
    public _activeModal: NgbActiveModal,
    private _translateService: TranslateService,
    private _userService: UserService,
    private _notificationService: NotificationService,
    private _queueService: QueueService,
    private _insightsService: InsightsService,
    private _authenticationService: AuthenticationService
  ) {
    this._unsubscribeAll = new Subject();
    this.statusList = [];
  }

  ngOnInit(): void {
    this.langEnglish = this._translateService.currentLang === "en";
  }

  /**
 * On Destroy
 */
  ngOnDestroy(): void {
    // Unsubscribe from all subscription
    this._unsubscribeAll.next(null);
    this._unsubscribeAll.complete();
  }

  /**
   * Función que se ejecuta después de cargar la página
   */
  ngAfterViewInit(): void {
    this.sectionBlockUI.start();

    this.setPage();
    // Obtiene el menú por su id
    this._appPermissionService.onPermissionsChanged.pipe(takeUntil(this._unsubscribeAll)).subscribe(resp => {
      if (resp) {
        Promise.resolve().then(() => {
          this.menu = this._appPermissionService.getMenuById(this.menuId);
          this._cdr.detectChanges();
        });
      }
    });
  }

  /**
   * Función para apertura de pagina
   */
  async setPage() {
    try {

      const allUserList = await this.getUsers();

      //cargamos cambios de estado por menu
      this.statusList = await this._statushChangeServive
        .getAllStatusChangeByMenu(this.menuId)
        .pipe(takeUntil(this._unsubscribeAll))
        .toPromise();

      //filtramos los estados
      this.statusList = this.statusList.filter(
        (i) =>
          i.newStatus !== null &&
          i.oldStatus !== null &&
          i.title !== "Descontinuar"
      );

      // Se recorre estado a estado para obtener los usuarios
      this.statusList.forEach((status, index) => {
        // Se declaran las variables vacías
        let userList: User[] = [];
        this.userSelected[index] = { users: [] };

        // Se recorre todas las collecciones que tiene asignada el usuario
        status.statusChangeCollection.forEach(statusChangeCollection => {
          const users = statusChangeCollection.status.code === 'ACT' && statusChangeCollection.collection.status.code === 'ACT' ? 
            statusChangeCollection.collection.collectionUsers.filter(collectionUsers => collectionUsers.status.code === 'ACT')
              .map(collectionUsers => collectionUsers.user).filter(user => !userList.some(u => u.id === user.id)) : [];
          userList = userList.concat(users);
        });

        userList = userList.length > 0 ? userList : allUserList;

        // Agrega el listado de usuarios al campo de usuarios
        status.users = userList.map(user => {
          user.fullname = user.person ? `${user.person.name} ${user.person.surname}` : `${user.name} ${user.surname}`;
          return user;
        });
      });

      //Verifica que no contenga usuarios ya el flujo de aprobaciones
      if (this.userSelectedInput.length != 0) {
        this.setDataWhitEdit();
      }

      this._cdr.detectChanges();
      this.sectionBlockUI.stop();
    } catch (error) {
      this.log('Error al cargar los estados y usuarios del flujo de aprobación', this.logData(), error, true);
      this.sectionBlockUI.stop();
      this._translateService
        .get(["COMMON.APPROVAL_FLOW.ERROR_CREATED"])
        .pipe(takeUntil(this._unsubscribeAll))
        .subscribe((value) => {
          this._swalService.showErrorMsg(
            value["COMMON.APPROVAL_FLOW.ERROR_CREATED"]
          );
        });
    }
  }

  /**
   * Obtiene un array de usuarios
   * @returns {Promise<User[]>} Devuelve un promesa de usuarios
   */
  async getUsers(): Promise<User[]> {
    return await this._userService.getFilteredUsers().pipe(takeUntil(this._unsubscribeAll)).toPromise();
  }

  /**
   * Función que sirve para cerrar el modal
   */
  closeModal() {
    this._activeModal.close();
  }

  /**
   * Código o nombre del objeto, para el subtítulo (es un dato, no se traduce)
   */
  get objectLabel(): string {
    return this.object?.code || this.object?.name || '';
  }

  /**
   * Cierra sin guardar (X y botón Cerrar)
   */
  cancel(): void {
    this.log('Cerró el flujo de aprobación sin guardar', this.logData());
    this._activeModal.close('Cross click');
  }

  /**
   * Función que se ejecuta cuando se selecciona un usuario
   * @param {any} event Indica el evento que se ejecuta
   * @param {number} index Indica el index del usuario que se esta seleccionado
   * @param {User[]} users Array de usuarios
   */
  onModelChange(event: any, index: number, users: User[]) {
    const selectedUsers = event.map((i) =>
      users.find((user) => user.id === i.id)
    );
    this.userSelected[index].users = selectedUsers;
  }

  /**
   * Función para almacenar la asignación de cambio de estado usuarios
   * @param form formulario de asignación de cambio de estado
   */
  save(form: NgForm) {
    //validamos formulario

    if (form.valid && this.getDataFromTable()) {
      this._swalService.showLoading();
      this._notificationService.createNotification(this.row).pipe(takeUntil(this._unsubscribeAll)).subscribe(
        (response) => {
          this.log('Guardó el flujo de aprobación', this.logData());
          this._swalService.close();
          this._translateService
            .get(["COMMON.APPROVAL_FLOW.SUCCESSFULLY_CREATED"])
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((value) => {
              this._swalService.showSuccessMsg(
                value["COMMON.APPROVAL_FLOW.SUCCESSFULLY_CREATED"]
              );
            });
          this.sendMessageToQueue(this.object, true);
          this._activeModal.close();
        },
        (error) => {
          this.log('Error al guardar el flujo de aprobación', this.logData(), error, true);
          this._translateService
            .get(["COMMON.APPROVAL_FLOW.ERROR_CREATED"])
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((value) => {
              this._swalService.showErrorMsg(
                value["COMMON.APPROVAL_FLOW.ERROR_CREATED"]
              );
            });
        }
      );
    }
  }

  /**
   * Función para recorrer tabla y asignar usuarios a cambios de estado
   */
  getDataFromTable(): boolean {
    this.row = [];
    let valid: boolean = true;
    this.statusList.forEach((i, index) => {
      var notificationId = undefined;

      // Busca un elemento en userSelectedInput que coincida con las condiciones de newStatus.code y oldStatus.code
      if (this.userSelectedInput.length > 0) {
        notificationId = this.userSelectedInput.find(n =>
          n.newStatus.code === i.newStatus.code && n.oldStatus.code === i.oldStatus.code
        )?.id; // Asigna el id si se encuentra una coincidencia
      }

      this.row.push({
        id: notificationId,
        object: this.object,
        objectId: this.object?.id.toString(),
        menu: {
          id: this.menu.id,
          title: this.menu.title,
        },
        newStatus: i.newStatus,
        oldStatus: i.oldStatus,
        users: this.userSelected[index].users.map((user) => ({
          id: user.id,
          email: user.email,
        })) as User[],
      });
      //validamos que cumpla con el minimo de aprobaciones
      if (this.userSelected[index].users.length < i.approvers) {
        valid = false;
      }
    });

    return valid;
  }

  /**
   * Función para enviar mensaje a cola
   * @param object mensaje
   * @param status si es false es un rechazo
   */
  sendMessageToQueue(object: any, status: boolean) {
    try {
      let message = {
        objectId: object.id,
        menuId: this.menu.id,
        oldStatusId: object.status.id,
        status: status,
      };
      // El error del observable no llega al catch (es asíncrono): se traza acá
      this._queueService.sendMessage(message, "notifications").pipe(takeUntil(this._unsubscribeAll)).subscribe(
        (response) => { },
        (error) => {
          this.log('Error al encolar las notificaciones del flujo de aprobación', this.logData(), error, true);
        }
      );
    } catch (error) {
      this.log('Error al armar el mensaje de notificaciones del flujo de aprobación', this.logData(), error, true);
    }
  }

  //Función para actualizar datos en el flujo de aprobaciones
  setDataWhitEdit() {
    // Itera sobre cada elemento en userSelectedInput
    this.userSelectedInput.forEach(input => {
      // Busca en statusList un elemento (status) que coincida con las condiciones
      this.statusList.find(status => {
        // Comprueba si newStatus.code y oldStatus.code coinciden con input
        if (status.newStatus.code === input.newStatus.code && status.oldStatus.code === input.oldStatus.code) {
          // Encuentra el índice de status en statusList
          var index = this.statusList.findIndex(p => p === status);

          // Itera sobre cada usuario en input.users
          input.users.forEach(user => {
            // Encuentra al usuario específico en statusList que coincida con el correo electrónico
            const foundUser = this.statusList.find(s => s.users.find(u => u.email === user.email))?.users.find(u => u.email === user.email);

            // Agrega el usuario encontrado a userSelected en la posición index
            this.userSelected[index].users.push(foundUser);
          });
        }
      });
    });
  }

  /**
   * Contexto para las trazas: ids y cantidades, no los usuarios
   */
  private logData(): any {
    return {
      objectId: this.object?.id ?? null,
      objectCode: this.object?.code ?? null,
      menuId: this.menu?.id ?? this.menuId ?? null,
      statuses: this.statusList?.length ?? 0,
      assignedUsers: this.userSelected.reduce((total, item) => total + (item?.users?.length ?? 0), 0),
      editing: (this.userSelectedInput?.length ?? 0) > 0,
    };
  }

  /**
   * Manda una traza a Application Insights, etiquetada con el proyecto y el
   * usuario que la originó.
   */
  private log(message: string, data: any, ex: any = null, error: boolean = false): void {
    const user = this._authenticationService.currentUserValue;

    if (error) {
      this._insightsService.traceError(environment.projects.StatusChange, message, data, ex, user);
      return;
    }

    this._insightsService.traceInfo(environment.projects.StatusChange, message, data, user);
  }

}
